// IndexedDB, not localStorage: the text pack runs to a few megabytes, which is
// over the localStorage limit on most browsers, and localStorage is synchronous
// — writing it would freeze the page.

const DB_NAME = 'quran-reader-offline';
const DB_VERSION = 1;
const STORE = 'packs';

export const isSupported = () => typeof indexedDB !== 'undefined';

const openDb = () => new Promise((resolve, reject) => {
  if (!isSupported()) {
    reject(new Error('This browser has no IndexedDB'));
    return;
  }
  const request = indexedDB.open(DB_NAME, DB_VERSION);
  request.onupgradeneeded = () => {
    const db = request.result;
    if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
  };
  request.onsuccess = () => resolve(request.result);
  request.onerror = () => reject(request.error);
});

const withStore = async (mode, work) => {
  const db = await openDb();
  try {
    return await new Promise((resolve, reject) => {
      const transaction = db.transaction(STORE, mode);
      const request = work(transaction.objectStore(STORE));
      transaction.onerror = () => reject(transaction.error);
      transaction.onabort = () => reject(transaction.error || new Error('Transaction aborted'));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  } finally {
    db.close();
  }
};

export const readPack = (key) => withStore('readonly', (store) => store.get(key));
export const writePack = (key, value) => withStore('readwrite', (store) => store.put(value, key));
export const removePack = (key) => withStore('readwrite', (store) => store.delete(key));

// What the device actually allows, rather than a guess. Quotas differ hugely
// between Chrome, Firefox and Safari, so the app should ask rather than assume.
export const storageEstimate = async () => {
  try {
    if (!navigator.storage?.estimate) return null;
    const { quota, usage } = await navigator.storage.estimate();
    return { quota: quota || 0, usage: usage || 0 };
  } catch (error) {
    return null;
  }
};

// Chrome grants this to installed apps; on iOS the reliable lever is adding the
// app to the home screen, which exempts it from the eviction of unused storage.
export const requestPersistence = async () => {
  try {
    if (!navigator.storage?.persist) return false;
    if (await navigator.storage.persisted()) return true;
    return await navigator.storage.persist();
  } catch (error) {
    return false;
  }
};

export const formatBytes = (bytes) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 MB';
  const mb = bytes / (1024 * 1024);
  if (mb < 1) return `${Math.round(bytes / 1024)} KB`;
  if (mb < 1024) return `${mb.toFixed(mb < 10 ? 1 : 0)} MB`;
  return `${(mb / 1024).toFixed(1)} GB`;
};
